/** The AI speaking partner — the speaking_topic player surface.
 *
 * The continuous-voice loop the product demands: press Start ONCE →
 * the partner greets you aloud (browser TTS) → the microphone arms
 * itself (browser speech recognition) → each finished utterance goes to
 * Claude → the reply is spoken → the mic re-arms. No per-turn button.
 * A typed-input row is always available (mic-less browsers, quiet
 * environments) and is also what the tests drive.
 *
 * Voice never leaves the browser — the server sees text turns only.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/exams";
import { Badge, Button, Spinner } from "../../components/ui";
import { TapToTranslate } from "../../components/TapToTranslate";
import { useT } from "../../i18n";
import { sttAvailable } from "../../utils/stt";
import { useVoiceConversation } from "./useVoiceConversation";
import { VoiceOrb } from "./VoiceOrb";

function TaskPrompt({ payload }: { payload: Record<string, unknown> }) {
  return (
    <div className="space-y-3">
      {typeof payload.source_text === "string" && payload.source_text && (
        <div className="rounded-control bg-canvas p-3 text-sm italic text-ink">
          <TapToTranslate block text={payload.source_text} />
        </div>
      )}
      <ul className="list-disc pl-5 text-sm text-ink">
        {((payload.points as string[]) ?? []).map((point) => (
          <li key={point}>
            <TapToTranslate text={point} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function CriterionBar({ label, score }: { label: string; score: number }) {
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span className="font-semibold text-ink">{label}</span>
        <span className="text-muted">{score} / 5</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-canvas">
        <div
          className="h-2 rounded-full bg-brand"
          style={{ width: `${(score / 5) * 100}%` }}
          aria-label={`${label} score ${score} of 5`}
        />
      </div>
    </div>
  );
}

export function Transcript({ turns }: { turns: api.SpeakingTurn[] }) {
  const t = useT();
  return (
    <ul className="space-y-2" aria-label="conversation transcript">
      {turns.map((turn, i) => (
        <li
          key={i}
          className={`max-w-[85%] rounded-control p-2 text-sm ${
            turn.role === "assistant"
              ? "bg-canvas text-ink"
              : "ml-auto bg-brand-soft text-brand-dark"
          }`}
        >
          <span className="block text-xs font-semibold text-muted">
            {turn.role === "assistant" ? t("speaking.partner") : t("speaking.you")}
          </span>
          {/* Only the partner's German is tappable — the learner's own words
              need no translating back to them. */}
          {turn.role === "assistant" ? <TapToTranslate text={turn.text} /> : turn.text}
        </li>
      ))}
    </ul>
  );
}

export function FeedbackView({
  session,
  onAgain,
}: {
  session: api.SpeakingSession;
  onAgain: () => void;
}) {
  const t = useT();
  const fb = session.feedback;
  return (
    <div className="space-y-5">
      {fb ? (
        <>
          <div className="text-center">
            <p className="text-4xl" aria-hidden>{fb.score_percent >= 60 ? "🎉" : "💪"}</p>
            <h2 className="mt-2 text-2xl font-extrabold">{t("speaking.feedback")}</h2>
            <p className="mt-1 text-lg">
              <strong data-testid="speaking-score">{fb.score_percent}%</strong>
              {" · "}
              {t("writing.levelEstimate")}: <strong>{fb.level_estimate}</strong>
            </p>
          </div>
          <div className="space-y-3">
            <CriterionBar label={t("speaking.interaction")} score={fb.interaction_score} />
            <CriterionBar label={t("speaking.vocabulary")} score={fb.vocabulary_score} />
            <CriterionBar label={t("speaking.grammar")} score={fb.grammar_score} />
          </div>
          {fb.corrections.length > 0 && (
            <div>
              <h3 className="font-bold text-ink">{t("writing.corrections")}</h3>
              <ul className="mt-2 space-y-2">
                {fb.corrections.map((c, i) => (
                  <li key={i} className="rounded-control bg-canvas p-2 text-sm">
                    <span className="text-danger line-through">{c.original}</span>{" "}
                    → <strong className="text-success">{c.corrected}</strong>
                    <p className="mt-0.5 text-muted">{c.explanation}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {fb.strengths.length > 0 && (
              <div>
                <h3 className="font-bold text-ink">{t("writing.strengths")}</h3>
                <ul className="mt-1 list-disc pl-5 text-sm text-ink">
                  {fb.strengths.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </div>
            )}
            {fb.improvements.length > 0 && (
              <div>
                <h3 className="font-bold text-ink">{t("writing.improvements")}</h3>
                <ul className="mt-1 list-disc pl-5 text-sm text-ink">
                  {fb.improvements.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
          {fb.overall_comment && (
            <p className="rounded-control bg-brand-soft p-3 text-sm text-brand-dark">
              {fb.overall_comment}
            </p>
          )}
        </>
      ) : (
        <p role="status" className="rounded-control bg-canvas p-3 text-sm text-muted">
          {t("speaking.tooShort")}
        </p>
      )}

      <details>
        <summary className="cursor-pointer text-sm font-semibold text-ink">
          {t("speaking.transcript")}
        </summary>
        <div className="mt-2">
          <Transcript turns={session.transcript} />
        </div>
      </details>

      <Button variant="secondary" onClick={onAgain}>
        {t("practice.tryAgain")}
      </Button>
    </div>
  );
}

export function SpeakingPartner({ itemId, payload }: { itemId: string; payload: Record<string, unknown> }) {
  const t = useT();
  const [history, setHistory] = useState<api.SpeakingHistory | null>(null);
  const [input, setInput] = useState("");

  const loadHistory = () => {
    api
      .speakingHistory(itemId)
      .then(setHistory)
      .catch(() => setHistory({ configured: false, quota: null, sessions: [] }));
  };

  useEffect(() => {
    let cancelled = false;
    api
      .speakingHistory(itemId)
      .then((h) => {
        if (!cancelled) setHistory(h);
      })
      .catch(() => {
        if (!cancelled) setHistory({ configured: false, quota: null, sessions: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  // The continuous-voice loop itself lives in the shared hook.
  const chat = useVoiceConversation({
    startSession: async () => {
      const r = await api.startSpeaking(itemId);
      setHistory((h) => (h ? { ...h, quota: r.quota } : h));
      return r;
    },
    onFinished: loadHistory,
  });
  const { session, messages, turns, busy, listening, error, ended } = chat;

  const onStart = () => void chat.start();
  const onEndConversation = () => void chat.end();
  const onAgain = () => chat.reset();
  const setEnded = chat.showEnded;

  const onSendTyped = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    chat.sendText(text);
  };

  if (!history) {
    return (
      <div className="grid place-items-center py-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (!history.configured) {
    return (
      <div className="space-y-3">
        <TaskPrompt payload={payload} />
        <p role="status" className="rounded-control bg-canvas p-3 text-sm text-muted">
          {t("speaking.notConfigured")}
        </p>
      </div>
    );
  }

  if (ended) {
    return <FeedbackView session={ended} onAgain={onAgain} />;
  }

  if (!session) {
    return (
      <div className="space-y-4">
        <TaskPrompt payload={payload} />
        {!sttAvailable() && (
          <p className="rounded-control bg-canvas p-3 text-xs text-muted">
            {t("speaking.micUnavailable")}
          </p>
        )}
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted">
            {history.quota && t("speaking.quotaUsed", {
              used: history.quota.used,
              limit: history.quota.limit,
            })}
          </div>
          <Button onClick={onStart} loading={busy}>
            🎤 {t("speaking.start")}
          </Button>
        </div>
        {history.sessions.length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-ink">{t("speaking.previous")}</h3>
            <ul className="mt-1 space-y-1">
              {history.sessions.map((s, i) => (
                <li key={s.id}>
                  <button
                    type="button"
                    aria-label={`view conversation ${i + 1}`}
                    onClick={() => setEnded(s)}
                    className="text-sm text-brand hover:underline"
                  >
                    {s.score_percent !== null ? `${s.score_percent}%` : "—"} ·{" "}
                    {s.turns} {t("speaking.turnsWord")}
                    {s.created_at ? ` · ${new Date(s.created_at).toLocaleDateString()}` : ""}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Badge tone="brand">{t("speaking.aiPartner")}</Badge>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Same orb as the free speaking bot — one conversation surface. */}
      <VoiceOrb state={busy ? "thinking" : listening ? "listening" : "speaking"} />
      <Transcript turns={messages} />

      <div
        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted"
        data-testid="speaking-status"
      >
        {busy && (
          <span className="inline-flex items-center gap-2">
            <Spinner size={14} /> {t("speaking.thinking")}
          </span>
        )}
        {!busy && listening && <span>🎙️ {t("speaking.listening")}</span>}
        <span className="ml-auto">
          {t("speaking.turns", { turns, max: session.maxTurns ?? "—" })}
        </span>
      </div>

      {error && <p role="alert" className="text-sm text-danger">{error}</p>}

      <div className="flex gap-2">
        <input
          aria-label={t("speaking.typeFallback")}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSendTyped();
          }}
          className="w-full min-w-0 rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
          placeholder={t("speaking.typeFallback")}
        />
        <Button variant="secondary" onClick={onSendTyped} disabled={busy || !input.trim()}>
          {t("speaking.send")}
        </Button>
      </div>

      <div className="text-right">
        <Button variant="danger" onClick={onEndConversation} loading={busy}>
          {t("speaking.end")}
        </Button>
      </div>
    </div>
  );
}
